import React, { useState, useRef } from "react";
import { Col, Row, Input, Label, FormFeedback, Button } from "reactstrap";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { createCourse, updatecourse } from "slices/Course/thunk";
import { AppDispatch } from "store";
import { AwsS3Uploader } from "Components/Common/AwsS3Uploader";
import appConfig from "config";

const COVER_IMAGE_FOLDER = "coverimage";

const s3Uploader = appConfig.s3?.accessKey && appConfig.s3?.secretKey
  ? new AwsS3Uploader({
      accessKey: appConfig.s3.accessKey,
      secretKey: appConfig.s3.secretKey,
      bucket: appConfig.s3.bucket,
      region: appConfig.s3.region || "nyc3",
      endpoint: appConfig.s3.endpoint || undefined,
    })
  : null;

interface CourseFormProps {
  validation: any;
  isEdit?: boolean;
  onSuccessNext?: () => void;
}

const CourseForm: React.FC<CourseFormProps> = ({ validation, isEdit = false, onSuccessNext }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const customSubmit = async (e: any) => {
    e.preventDefault();

    if (saving) {
      return;
    }

    const errors = await validation.validateForm();

    Object.keys(errors).forEach((field) => {
      validation.setFieldTouched(field, true, false);
    });

    if (Object.keys(errors).length > 0) {
      toast.error("Please fill all required fields.");
      return;
    }

    const values = validation.values;

    try {
      setSaving(true);
      if (isEdit && values?.course_id) {
        await dispatch(updatecourse(values.course_id, values));
        toast.success("Course Updated successfully!");
      } else {
        const response = await dispatch(createCourse(values) as any);
        if (response?.data) {
          validation.setFieldValue("course_id", response.data);
        }
        toast.success("Course added successfully!");
      }

      if (onSuccessNext) onSuccessNext();
    } catch (error) {
      console.error("Error saving course:", error);
      toast.error("Failed to save course. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={customSubmit} noValidate>
      <div className="px-3 py-2">
        <Row className="g-3 mt-2">
          <Col md={12}>
            <Label>Course Name <span className="text-danger">*</span></Label>
            <Input
              name="course_name"
              value={validation.values.course_name}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.course_name && validation.errors.course_name)}
              placeholder="Type course name..."
              maxLength={800}
            />
            <FormFeedback>{validation.errors.course_name}</FormFeedback>
          </Col>

          <Col md={6}>
            <Label>Trainer <span className="text-danger">*</span></Label>
            <Input
              name="trainer"
              value={validation.values.trainer}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.trainer && validation.errors.trainer)}
              placeholder="Trainer name"
              maxLength={100}
            />
            <FormFeedback>{validation.errors.trainer}</FormFeedback>
          </Col>

          <Col md={6}>
            <Label>Modules <span className="text-danger">*</span></Label>
            <Input
              name="noofmodule"
              value={validation.values.noofmodule}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.noofmodule && validation.errors.noofmodule)}
              placeholder="No. of modules"
              maxLength={8}
            />
            <FormFeedback>{validation.errors.noofmodule}</FormFeedback>
          </Col>

          <Col md={6}>
            <Label>Hours per day</Label>
            <Input
              name="hourperday"
              value={validation.values.hourperday}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.hourperday && validation.errors.hourperday)}
              placeholder="12"
              maxLength={8}
            />
            <FormFeedback>{validation.errors.hourperday}</FormFeedback>
          </Col>

          <Col md={6}>
            <Label>Week</Label>
            <Input
              name="noofweek"
              value={validation.values.noofweek}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.noofweek && validation.errors.noofweek)}
              placeholder="04"
              maxLength={8}
            />
            <FormFeedback>{validation.errors.noofweek}</FormFeedback>
          </Col>
        </Row>
       <Row className="g-3 mt-2">
          <Col md={6}>
            <Label>Cover Image <span className="text-danger">*</span></Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              name="thumbnail_url"
              className="d-none"
              disabled={uploadingCover}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (!s3Uploader) {
                  toast.error("S3 upload is not configured.");
                  return;
                }
                setUploadingCover(true);
                try {
                  const contentType = file.type || "image/jpeg";
                  const url = await s3Uploader.uploadFile(file, {
                    serviceName: COVER_IMAGE_FOLDER,
                    contentType,
                  });
                  if (url) {
                    validation.setFieldValue("thumbnail_url", url);
                    validation.setFieldTouched("thumbnail_url", true);
                    validation.setFieldError("thumbnail_url", undefined);
                    await validation.validateField("thumbnail_url");
                  } else {
                    toast.error("Cover image upload failed. Please try again.");
                  }
                } catch {
                  toast.error("Cover image upload failed. Please try again.");
                } finally {
                  setUploadingCover(false);
                  e.target.value = "";
                }
              }}
            />
            <div
              role="button"
              tabIndex={0}
              onClick={() => !uploadingCover && fileInputRef.current?.click()}
              onKeyDown={(e) => e.key === "Enter" && !uploadingCover && fileInputRef.current?.click()}
              title={validation.values.thumbnail_url || undefined}
              className={`form-control d-flex align-items-center p-0 overflow-hidden ${validation.touched.thumbnail_url && validation.errors.thumbnail_url && !validation.values.thumbnail_url ? "is-invalid" : ""}`}
              style={{ cursor: uploadingCover ? "not-allowed" : "pointer", minHeight: "38px" }}
            >
              <span
                className="px-3 py-2 bg-light border-end text-nowrap"
                style={{ fontSize: "inherit" }}
              >
                Choose File
              </span>
              <span className="px-2 py-2 text-muted text-truncate small flex-grow-1" style={{ minWidth: 0 }}>
                {uploadingCover ? "Uploading..." : validation.values.thumbnail_url || "No file chosen"}
              </span>
            </div>
            {validation.touched.thumbnail_url && validation.errors.thumbnail_url && !validation.values.thumbnail_url && (
              <FormFeedback>{validation.errors.thumbnail_url}</FormFeedback>
            )}

            {validation.values.thumbnail_url && (
              <div className="mt-2">
                <img
                  src={validation.values.thumbnail_url}
                  alt="preview"
                  style={{ width: "120px", height: "auto", borderRadius: "6px" }}
                />
              </div>
            )}
          </Col>

          <Col md={3}>
            <Label>Discount Type</Label>
            <Input
              type="select"
              name="discount_type"
              value={validation.values.discount_type}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.discount_type && validation.errors.discount_type)}
            >
              <option value="">Select</option>
              <option value="Percentage">Percentage</option>
              <option value="Fixed">Fixed</option>
            </Input>
            <FormFeedback>{validation.errors.discount_type}</FormFeedback>
          </Col>
          <Col md={3}>
            <Label>Discount Value</Label>
            <Input
              name="discount_value"
              type="text"
              inputMode="decimal"
              value={validation.values.discount_value}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d.]/g, "");
                const parts = raw.split(".");
                const integer = parts[0].slice(0, 8);
                const decimal = parts[1] !== undefined ? "." + parts[1].slice(0, 3) : "";
                const value = parts.length > 1 ? integer + decimal : integer;
                validation.setFieldValue("discount_value", value);
              }}
              invalid={!!(validation.touched.discount_value && validation.errors.discount_value)}
              placeholder="Enter value"
              maxLength={12}
            />
            <FormFeedback>{validation.errors.discount_value}</FormFeedback>
          </Col>
        </Row>
        <Row className="g-3 mt-2">
          <Col md={6}>
            <Label>Duration <span className="text-danger">*</span></Label>
            <Input
              name="duration"
              value={validation.values.duration}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.duration && validation.errors.duration)}
              placeholder="e.g. 40 hrs"
              maxLength={12}
            />
            <FormFeedback>{validation.errors.duration}</FormFeedback>
          </Col>

          <Col md={6}>
            <Label>Course Level <span className="text-danger">*</span></Label>
            <Input
              type="select"
              name="course_level"
              value={validation.values.course_level}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.course_level && validation.errors.course_level)}
            >
              <option value="">Select Level</option>
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
            </Input>
            <FormFeedback>{validation.errors.course_level}</FormFeedback>
          </Col>
        </Row>

        <Row className="g-3 mt-3">
          <Col md={6}>
            <Label>Course Price <span className="text-danger">*</span></Label>
            <Input
              name="price"
              type="text"
              inputMode="decimal"
              value={validation.values.price}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d.]/g, "");
                const parts = raw.split(".");
                const integer = parts[0].slice(0, 8);
                const decimal = parts[1] !== undefined ? "." + parts[1].slice(0, 3) : "";
                const value = parts.length > 1 ? integer + decimal : integer;
                validation.setFieldValue("price", value);
              }}
              invalid={!!(validation.touched.price && validation.errors.price)}
              placeholder="Enter price"
              maxLength={12}
            />
            <FormFeedback>{validation.errors.price}</FormFeedback>
          </Col>
          <Col md={6}>
            <Label>Description <span className="text-danger">*</span></Label>
            <Input
              type="textarea"
              name="description"
              value={validation.values.description}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.description && validation.errors.description)}
              placeholder="Write about the course..."
              rows={3}
              maxLength={4000}
            />
            <FormFeedback>{validation.errors.description}</FormFeedback>
          </Col>
        </Row>

        <div className="d-flex justify-content-end mt-4">
          <Button 
            color="primary" 
            type="submit" 
            disabled={saving}
            style={{ 
              minWidth: "150px",
              opacity: saving ? 0.7 : 1,
              cursor: saving ? "not-allowed" : "pointer"
            }}
          >
            {saving ? (
              <>
                <i className="fa fa-spinner fa-spin me-2" />
                {isEdit ? "Updating..." : "Saving..."}
              </>
            ) : (
              isEdit ? "Update" : "Save & Continue"
            )}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CourseForm;
